import { imul, smi } from './immath.js'

import {
  imerrValueCannotBeHashed,
  imerrInvalidKeyNonExtensible
} from './imerr.js'

const defaultValueOf = Object.prototype.valueOf

const valueOf = obj => {
  return obj.valueOf !== defaultValueOf && typeof obj.valueOf === 'function'
    ? obj.valueOf(obj)
    : obj
}

function hash(o) {
  if (o == null) {
    return hashNullish(o)
  }

  /*
  if (typeof o.hashCode === 'function') {
    // Drop any high bits from accidentally long hash codes.
    return smi(o.hashCode(o))
  }
   */
  const v = valueOf(o)
  switch (typeof v) {
  case 'boolean':
    // The hash values for built-in constants are a 1 value for each 5-byte
    // shift region expect for the first, which encodes the value. This
    // reduces the odds of a hash collision for these common values.
    return v ? 0x42108421 : 0x42108420
  case 'number':
    return hashNumber(v)
  case 'string':
    return v.length > STRING_HASH_CACHE_MIN_STRLEN
      ? cachedHashString(v)
      : hashString(v)
  case 'object':
    return v === null
      ? hashNullish(v) : hashJSObj(v)
  // case 'function':
  //   return hashJSObj(v)
  // case 'symbol':
  //   return hashSymbol(v)
  default:
    //   if (typeof v.toString === 'function') {
    //     return hashString(v.toString())
    //   }
    throw imerrValueCannotBeHashed(v)
  }
}

function hashNullish(nullish) {
  return nullish === null ? 0x42108422 : /* undefined */ 0x42108423
}

// Compress arbitrarily large numbers into smi hashes.
function hashNumber(n) {
  if (n !== n || n === Infinity) {
    return 0
  }
  let hash = n | 0
  if (hash !== n) {
    hash ^= n * 0xffffffff
  }
  while (n > 0xffffffff) {
    n /= 0xffffffff
    hash ^= n
  }
  return smi(hash)
}

function cachedHashString(string) {
  let hashed = stringHashCache[string]
  if (hashed === undefined) {
    hashed = hashString(string)
    if (STRING_HASH_CACHE_SIZE === STRING_HASH_CACHE_MAX_SIZE) {
      STRING_HASH_CACHE_SIZE = 0
      stringHashCache = {}
    }
    STRING_HASH_CACHE_SIZE++
    stringHashCache[string] = hashed
  }
  return hashed
}

// http://jsperf.com/hashing-strings
function hashString(string) {
  // This is the hash from JVM
  // The hash code for a string is computed as
  // s[0] * 31 ^ (n - 1) + s[1] * 31 ^ (n - 2) + ... + s[n - 1],
  // where s[i] is the ith character of the string and n is the length of
  // the string. We "mod" the result to make it between 0 (inclusive) and 2^31
  // (exclusive) by dropping high bits.
  let hashed = 0
  for (let ii = 0; ii < string.length; ii++) {
    hashed = (31 * hashed + string.charCodeAt(ii)) | 0
  }
  return smi(hashed)
}
/*
function hashSymbol(sym) {
  let hashed = symbolMap[sym]
  if (hashed !== undefined) {
    return hashed
  }

  hashed = nextHash()

  symbolMap[sym] = hashed

  return hashed
}
 */
function hashJSObj(obj) {
  let hashed
  if (weakMap) {
    hashed = weakMap.get(obj)
    if (hashed !== undefined) {
      return hashed
    }
  }

  hashed = obj[UID_HASH_KEY]
  if (hashed !== undefined) {
    return hashed
  }

  hashed = nextHash()

  if (weakMap) {
    weakMap.set(obj, hashed)
  } else if (Object.isExtensible(obj) === false) {
    throw imerrInvalidKeyNonExtensible()
  } else {
    Object.defineProperty(obj, UID_HASH_KEY, {
      enumerable: false,
      configurable: false,
      writable: false,
      value: hashed
    })
  }

  return hashed
}

function nextHash() {
  const nextHash = ++_objHashUID
  if (_objHashUID & 0x40000000) {
    _objHashUID = 0
  }
  return nextHash
}

// If possible, use a WeakMap.
let weakMap = typeof WeakMap === 'function' && new WeakMap(),
    _objHashUID = 0,
    UID_HASH_KEY = typeof Symbol === 'function'
      ? Symbol('__immutablehash__')
      : '__immutablehash__',
    STRING_HASH_CACHE_SIZE = 0,
    stringHashCache = {}

// const symbolMap = Object.create(null)

// if (typeof Symbol === 'function') {
//   UID_HASH_KEY = Symbol(UID_HASH_KEY)
// }
const STRING_HASH_CACHE_MIN_STRLEN = 16
const STRING_HASH_CACHE_MAX_SIZE = 255

const hashMurmurHashOfSize = (size, h) => {
  h = imul(h, 0xcc9e2d51)
  h = imul((h << 15) | (h >>> -15), 0x1b873593)
  h = imul((h << 13) | (h >>> -13), 5)
  h = ((h + 0xe6546b64) | 0) ^ size
  h = imul(h ^ (h >>> 16), 0x85ebca6b)
  h = imul(h ^ (h >>> 13), 0xc2b2ae35)
  h = smi(h ^ (h >>> 16))
  return h
}

const hashMerge = (a, b) => {
  return (a ^ (b + 0x9e3779b9 + (a << 6) + (a >> 2))) | 0 // int
}

export {
  hash,
  hashMerge,
  hashMurmurHashOfSize
}
